import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
    ModalOverlay,
    ModalContent,
    ModalInnerContent,
    HeaderLeft,
    ModalHeader,
    TodoList,
    TodoItem,
    NewTodoItem,
    Checkbox,
    TodoInput,
    CloseButton,
    DeleteButton
} from '../styles/ProjectChecklistModal.styles';
import { ViewIcon } from '../components/icons/ViewIcon';
import { VisibilityToggleIcon } from '../components/icons/VisibilityToggleIcon';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { getContrastYIQ } from '../utils/textUtils';

const ProjectChecklistModal = ({ project, onClose }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const textColor = project.background?.color && !project.background?.urls?.thumb ? getContrastYIQ(project.background.color) : null;
    const [todos, setTodos] = useState(project.todos || []);
    const [newTodoText, setNewTodoText] = useState('');
    const [editingTodo, setEditingTodo] = useState(null);
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [showCompleted, setShowCompleted] = useState(() => {
        const saved = localStorage.getItem(`project-${project.id}-show-completed`);
        return saved !== null ? JSON.parse(saved) : false;
    });
    const [newlyAddedItemId, setNewlyAddedItemId] = useState(null);
    const inputRefs = useRef({});

    const handleOpenProjectBoard = () => {
        navigate(`/project/${project.id}`);
        onClose();
    };

    useEffect(() => {
        if (newlyAddedItemId && inputRefs.current[newlyAddedItemId]) {
            const inputElement = inputRefs.current[newlyAddedItemId];
            inputElement.focus();
            inputElement.selectionStart = inputElement.selectionEnd = inputElement.value.length;
            setNewlyAddedItemId(null);
        }
    }, [newlyAddedItemId]);

    useEffect(() => {
        localStorage.setItem(`project-${project.id}-show-completed`, JSON.stringify(showCompleted));
    }, [showCompleted, project.id]);

    const handleAddTodo = async (options = {}) => {
        const { text = '', index = -1, fromEvent = null } = options;
        fromEvent?.preventDefault();

        const content = index === -1 ? (text || newTodoText.trim()) : text;

        if (index === -1 && !content) return;

        const newTodo = {
            id: `todo-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            text: content,
            completed: false,
            createdAt: new Date().toISOString(),
            createdBy: user.uid
        };

        const updatedTodos = [...todos];
        if (index > -1) {
            updatedTodos.splice(index, 0, newTodo);
        } else {
            updatedTodos.push(newTodo);
        }
        setTodos(updatedTodos);
        setNewTodoText('');
        setNewlyAddedItemId(newTodo.id);

        try {
            await updateDoc(doc(db, 'projects', project.id), {
                todos: updatedTodos,
                updatedAt: new Date().toISOString()
            });
        } catch (error) {
            console.error('Error adding todo:', error);
        }
    };

    const handleToggleTodo = async (todoId) => {
        const updatedTodos = todos.map(todo => {
            if (todo.id === todoId) {
                return { ...todo, completed: typeof todo.completed === 'boolean' ? !todo.completed : true };
            }
            return todo;
        });

        setTodos(updatedTodos);

        try {
            await updateDoc(doc(db, 'projects', project.id), {
                todos: updatedTodos,
                updatedAt: new Date().toISOString()
            });
        } catch (error) {
            console.error('Error toggling todo:', error);
        }
    };

    const handleDeleteTodo = async (todoId) => {
        const updatedTodos = todos.filter(todo => todo.id !== todoId);
        setTodos(updatedTodos);

        try {
            await updateDoc(doc(db, 'projects', project.id), {
                todos: updatedTodos,
                updatedAt: new Date().toISOString()
            });
        } catch (error) {
            console.error('Error deleting todo:', error);
        }
    };

    const handleUpdateTodo = async (todoId, newText) => {
        if (!newText.trim()) {
            handleDeleteTodo(todoId);
            return;
        }

        const updatedTodos = todos.map(todo => {
            if (todo.id === todoId) {
                return { ...todo, text: newText };
            }
            return todo;
        });

        setTodos(updatedTodos);

        try {
            await updateDoc(doc(db, 'projects', project.id), {
                todos: updatedTodos,
                updatedAt: new Date().toISOString()
            });
        } catch (error) {
            console.error('Error updating todo:', error);
        }
    };

    const onDragEnd = async (result) => {
        const { destination, source } = result;

        if (!destination) return;

        if (
            destination.droppableId === source.droppableId &&
            destination.index === source.index
        ) {
            return;
        }

        // Get visible todos and their mapping to the full array
        const visibleTodos = todos
            .map((todo, index) => ({ todo, index }))
            .filter(item => showCompleted || !item.todo.completed);

        // Get the actual indices from the full array
        const sourceIndex = visibleTodos[source.index].index;
        const destinationIndex = visibleTodos[destination.index]?.index ?? todos.length;

        // Create new array and perform the move
        const newTodos = Array.from(todos);
        const [removed] = newTodos.splice(sourceIndex, 1);
        newTodos.splice(destinationIndex, 0, removed);

        setTodos(newTodos);

        try {
            await updateDoc(doc(db, 'projects', project.id), {
                todos: newTodos,
                updatedAt: new Date().toISOString()
            });
        } catch (error) {
            console.error('Error reordering todos:', error);
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (index !== undefined) {
                const visibleTodo = visibleTodos[index];
                const realIndex = todos.findIndex(todo => todo.id === visibleTodo.id);
                handleAddTodo({ index: realIndex + 1 });
            } else {
                handleAddTodo({ fromEvent: e });
            }
        }
    };

    // Get filtered todos for rendering
    const visibleTodos = todos.filter(todo => showCompleted || !todo.completed);

    return (
        <ModalOverlay onClick={onClose}>
            <ModalContent
                onClick={e => e.stopPropagation()}
                background={project.background?.urls?.regular}
                backgroundColor={project.background?.color}
            >
                <ModalInnerContent>
                    <ModalHeader textColor={textColor}>
                        <h2 style={{marginRight: '8px'}}>{project.title}</h2>
                        <div>
                            <span style={{ color: textColor || '#fff', marginRight: '16px', display: 'flex', alignItems: 'center' }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={textColor || 'currentColor'} strokeWidth="2" style={{ marginRight: '4px' }}>
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                                {todos.filter(todo => todo.completed).length}/{todos.length}
                            </span>
                            <CloseButton as="button" onClick={handleOpenProjectBoard} title="Open project board" style={{padding: 0, fontSize: '18px', lineHeight: '1', marginRight: '8px', display: 'flex', alignItems: 'center'}}>
                                <ViewIcon style={{color: textColor || '#fff', transition: 'color 0.2s ease', '&:hover': { color: '#ddd' }}}/>
                            </CloseButton>
                            <CloseButton
                                as="button"
                                onClick={() => setShowCompleted(!showCompleted)}
                                title={showCompleted ? "Hide checked items" : "Show checked items"}
                                style={{padding: 0, fontSize: '18px', lineHeight: '1', display: 'flex', alignItems: 'center'}}
                                textColor={textColor}
                            >
                                <VisibilityToggleIcon hidden={!showCompleted} textColor={textColor} />
                            </CloseButton>
                            <CloseButton onClick={onClose} textColor={textColor}>×</CloseButton>
                        </div>
                    </ModalHeader>
                    <div style={{ marginBottom: '8px', padding: '0 16px' }}>
                        <TodoInput
                            value={newTodoText}
                            onChange={(e) => setNewTodoText(e.target.value)}
                            onKeyDown={handleKeyDown}
                            onBlur={() => {
                                if (newTodoText.trim()) {
                                    handleAddTodo();
                                }
                            }}
                            placeholder="Add item..."
                            style={{ width: '100%', color: textColor || '#fff', borderBottom: '1px solid #444' }}
                            textColor={textColor}
                        />
                    </div>

                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="todos">
                            {(provided) => (
                                <TodoList
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                >
                                    {visibleTodos.map((todo, index) => (
                                        <Draggable
                                            key={todo.id}
                                            draggableId={todo.id}
                                            index={index}
                                        >
                                            {(provided, snapshot) => (
                                                <TodoItem
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    isDragging={snapshot.isDragging}
                                                >
                                                    <div {...provided.dragHandleProps}>
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M3 3C4.1 3 5 3.9 5 5C5 6.1 4.1 7 3 7C1.9 7 1 6.1 1 5C1 3.9 1.9 3 3 3ZM10 3C11.1 3 12 3.9 12 5C12 6.1 11.1 7 10 7C8.9 7 8 6.1 8 5C8 3.9 8.9 3 10 3ZM3 10C4.1 10 5 10.9 5 12C5 13.1 4.1 14 3 14C1.9 14 1 13.1 1 12C1 10.9 1.9 10 3 10ZM10 10C11.1 10 12 10.9 12 12C12 13.1 11.1 14 10 14C8.9 14 8 13.1 8 12C8 10.9 8.9 10 10 10ZM3 17C4.1 17 5 17.9 5 19C5 20.1 4.1 21 3 21C1.9 21 1 20.1 1 19C1 17.9 1.9 17 3 17ZM10 17C11.1 17 12 17.9 12 19C12 20.1 11.1 21 10 21C8.9 21 8 20.1 8 19C8 17.9 8.9 17 10 17Z" fill={textColor || '#fff'}/>
                                                        </svg>
                                                    </div>
                                                    <Checkbox
                                                        type="checkbox"
                                                        checked={todo.completed}
                                                        onChange={() => handleToggleTodo(todo.id)}
                                                        textColor={textColor}
                                                    />
                                                    <TodoInput
                                                        as="textarea"
                                                        value={editingTodo?.id === todo.id ? editingTodo.text : todo.text}
                                                        onFocus={() => setEditingTodo({ id: todo.id, text: todo.text })}
                                                        onBlur={() => {
                                                            if (editingTodo) {
                                                                handleUpdateTodo(editingTodo.id, editingTodo.text);
                                                                setEditingTodo(null);
                                                            }
                                                        }}
                                                        onKeyDown={(e) => handleKeyDown(e, index)}
                                                        onChange={(e) => {
                                                            if (editingTodo) {
                                                                setEditingTodo({ ...editingTodo, text: e.target.value });
                                                            }
                                                            e.target.style.height = '24px';
                                                            e.target.style.height = `${e.target.scrollHeight}px`;
                                                        }}
                                                        completed={todo.completed}
                                                        style={{ overflowY: 'hidden' }}
                                                        ref={(el) => {
                                                            inputRefs.current[todo.id] = el;
                                                            if (el) {
                                                                el.style.height = '24px';
                                                                el.style.height = `${el.scrollHeight}px`;
                                                            }
                                                        }}
                                                        textColor={textColor}
                                                    />
                                                    <DeleteButton
                                                        onClick={() => handleDeleteTodo(todo.id)}
                                                        textColor={textColor}
                                                    >
                                                        ×
                                                    </DeleteButton>
                                                </TodoItem>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                    <NewTodoItem isFocused={isInputFocused}>
                                        <Checkbox type="checkbox" disabled textColor={textColor} />
                                        <TodoInput
                                            value={newTodoText}
                                            onChange={(e) => setNewTodoText(e.target.value)}
                                            onKeyDown={handleKeyDown}
                                            onFocus={() => setIsInputFocused(true)}
                                            onBlur={() => {
                                                setIsInputFocused(false);
                                                if (newTodoText.trim()) {
                                                    handleAddTodo({text: newTodoText});
                                                }
                                            }}
                                            placeholder="Add item..."
                                        />
                                    </NewTodoItem>
                                </TodoList>
                            )}
                        </Droppable>
                    </DragDropContext>
                </ModalInnerContent>
            </ModalContent>
        </ModalOverlay>
    );
};

export default ProjectChecklistModal;
